import type { Spec } from "@chickytutor/whiteboard-stream-renderer";
import type { NativeExercise } from "./native";
import { validateCompactBoard } from "./structured";
import { textLimit } from "./textLimits";

/** Match Python's composed-board budgets; hidden help has its own ceiling. */
export function validateComposition(spec: Spec, exercises: Record<string, NativeExercise>) {
  const tasks = Object.values(exercises);
  if (tasks.length > 1) throw new Error("Compact task count");
  const content = Object.entries(spec.elements)
    .filter(([id, item]) => id !== "board" && !["Activity", "Exercise"].includes(item.type))
    .map(([, item]) => item);
  const count = (type: string) => content.filter((item) => item.type === type).length;
  if (content.length > 5 || count("Heading") > 1 || count("Reveal") > 1)
    throw new Error("Composition support budget");
  if (count("Panel") + count("Stack") > 1) throw new Error("Composition group budget");
  const reading = tasks.some((unit) => unit.spec.kind === "reading_story");
  if (reading && content.some((item) => !["Heading", "Text"].includes(item.type)))
    throw new Error("Reading-only content");
  let visible = 0;
  const size = (text: unknown) => [...String(text)].length;
  for (const item of content) {
    if (["Stack", "Panel"].includes(item.type)) {
      const children = item.children ?? [];
      if (
        children.length < 1 ||
        children.length > 2 ||
        children.some(
          (id) => spec.elements[id] && !["Text", "Table"].includes(spec.elements[id].type)
        )
      )
        throw new Error("Composition group budget");
    }
    if (item.type === "Heading" && size(item.props.text) > textLimit(60))
      throw new Error("Composition text budget");
    if (item.type === "Text") visible += size(item.props.text);
    if (item.type === "Table") {
      const headers = item.props.headers as string[];
      const rows = item.props.rows as string[][];
      if (headers.length > 2 || rows.length > 6) throw new Error("Compact collection budget");
      visible += [headers, ...rows].flat().reduce((total, text) => total + size(text), 0);
    }
    if (
      item.type === "Reveal" &&
      (size(item.props.text) > textLimit(480) || size(item.props.label) > textLimit(60))
    )
      throw new Error("Composition text budget");
  }
  if (
    count("Text") + count("Table") > 2 ||
    visible > textLimit(!tasks.length || reading ? 500 : 250)
  )
    throw new Error("Composition text budget");
  validateCompactBoard({
    spec: { root: "board", elements: { board: { type: "Stack", props: {}, children: [] } } },
    exercises,
  });
}
